import { storableError } from '../../util/errors';
import { availabilityExceptionsAPI } from '../../api/clients/availabilityExceptionsAPI';
import { calendarInvitesAPI } from '../../api/clients/calendarInvitesAPI';
import { fetchCurrentUser } from '../../ducks/user.duck';
import config from '../../config';
import { episodeKeyOf } from '../../util/episodes';

/**
 * A creator's own manual events live on their privateData.
 *
 * Until 1 Sep 2026 they were on publicData, which is readable by anyone on
 * the marketplace: titles, descriptions, meeting links and, once invites
 * exist, who is coming. Reads fall back to the old location so nothing
 * disappears during cut-over; every write goes to privateData and removes the
 * public copy in the same call (Sharetribe deletes an extended-data key when
 * it is set to null), so the first edit self-migrates the account.
 */
const readOwnCalendarEvents = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  const priv = profile.privateData?.calendarEvents;
  if (Array.isArray(priv)) return priv;
  const pub = profile.publicData?.calendarEvents;
  return Array.isArray(pub) ? pub : [];
};

const ownCalendarEventsWrite = (currentUser, calendarEvents) => {
  const hasPublicCopy = Array.isArray(
    currentUser?.attributes?.profile?.publicData?.calendarEvents
  );
  return {
    privateData: { calendarEvents },
    ...(hasPublicCopy ? { publicData: { calendarEvents: null } } : {}),
  };
};


/**
 * Invitations go out from the server once the event is on the profile, so
 * what is mailed is exactly what was saved. Not sending is never an error for
 * the save itself: the event is already stored, and the next save tries
 * anyone still owed an invitation again.
 */
const sendInvitesIfWanted = async event => {
  if (!event?.id || event.sendInvites === false) return;
  if (!Array.isArray(event.attendees) || event.attendees.length === 0) return;
  try {
    await calendarInvitesAPI.sendCreatorInvites(event.id);
  } catch (e) {
    console.warn('Calendar invitations were not sent:', e?.message || e);
  }
};

// ================ Action types ================ //

export const FETCH_CALENDAR_EVENTS_REQUEST = 'app/CreatorCalendarPage/FETCH_CALENDAR_EVENTS_REQUEST';
export const FETCH_CALENDAR_EVENTS_SUCCESS = 'app/CreatorCalendarPage/FETCH_CALENDAR_EVENTS_SUCCESS';
export const FETCH_CALENDAR_EVENTS_ERROR = 'app/CreatorCalendarPage/FETCH_CALENDAR_EVENTS_ERROR';

// User event CRUD actions
export const CREATE_USER_EVENT_REQUEST = 'app/CreatorCalendarPage/CREATE_USER_EVENT_REQUEST';
export const CREATE_USER_EVENT_SUCCESS = 'app/CreatorCalendarPage/CREATE_USER_EVENT_SUCCESS';
export const CREATE_USER_EVENT_ERROR = 'app/CreatorCalendarPage/CREATE_USER_EVENT_ERROR';

export const UPDATE_USER_EVENT_REQUEST = 'app/CreatorCalendarPage/UPDATE_USER_EVENT_REQUEST';
export const UPDATE_USER_EVENT_SUCCESS = 'app/CreatorCalendarPage/UPDATE_USER_EVENT_SUCCESS';
export const UPDATE_USER_EVENT_ERROR = 'app/CreatorCalendarPage/UPDATE_USER_EVENT_ERROR';

export const DELETE_USER_EVENT_REQUEST = 'app/CreatorCalendarPage/DELETE_USER_EVENT_REQUEST';
export const DELETE_USER_EVENT_SUCCESS = 'app/CreatorCalendarPage/DELETE_USER_EVENT_SUCCESS';
export const DELETE_USER_EVENT_ERROR = 'app/CreatorCalendarPage/DELETE_USER_EVENT_ERROR';

// Delete episode actions
export const DELETE_EPISODE_REQUEST = 'app/CreatorCalendarPage/DELETE_EPISODE_REQUEST';
export const DELETE_EPISODE_SUCCESS = 'app/CreatorCalendarPage/DELETE_EPISODE_SUCCESS';
export const DELETE_EPISODE_ERROR = 'app/CreatorCalendarPage/DELETE_EPISODE_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  // True after the first successful fetch; never reset. Lets the page
  // show the loader only on the true first load while background
  // refetches keep the rendered calendar on screen (the Org calendar
  // duck had this from the start — this one was missing it, which is
  // why the creator calendar spinnered on every navigation).
  fetchLoaded: false,
  fetchError: null,
  events: [],
  listingRefs: [],
  // User event CRUD states
  createUserEventInProgress: false,
  createUserEventError: null,
  updateUserEventInProgress: false,
  updateUserEventError: null,
  deleteUserEventInProgress: false,
  deleteUserEventError: null,
  // Episode delete state
  deleteEpisodeInProgress: false,
  deleteEpisodeError: null,
  // Create-event participant options (audience lists)
  participantOptions: [],
};

export const SET_PARTICIPANT_OPTIONS = 'app/CreatorCalendarPage/SET_PARTICIPANT_OPTIONS';
export const setParticipantOptions = options => ({
  type: SET_PARTICIPANT_OPTIONS,
  payload: options,
});

export default function creatorCalendarPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_PARTICIPANT_OPTIONS:
      return { ...state, participantOptions: payload };
    case FETCH_CALENDAR_EVENTS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_CALENDAR_EVENTS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchLoaded: true,
        events: payload.events,
        listingRefs: payload.listingRefs,
      };
    case FETCH_CALENDAR_EVENTS_ERROR:
      console.error(payload);
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };

    // Create user event
    case CREATE_USER_EVENT_REQUEST:
      return {
        ...state,
        createUserEventInProgress: true,
        createUserEventError: null,
      };
    case CREATE_USER_EVENT_SUCCESS:
      return {
        ...state,
        createUserEventInProgress: false,
        // Add the new event to the events array
        events: [...state.events, payload.event],
      };
    case CREATE_USER_EVENT_ERROR:
      return {
        ...state,
        createUserEventInProgress: false,
        createUserEventError: payload,
      };

    // Update user event
    case UPDATE_USER_EVENT_REQUEST:
      return {
        ...state,
        updateUserEventInProgress: true,
        updateUserEventError: null,
      };
    case UPDATE_USER_EVENT_SUCCESS:
      return {
        ...state,
        updateUserEventInProgress: false,
        // Replace the updated event in the events array
        events: state.events.map(e =>
          e.id === payload.event.id ? payload.event : e
        ),
      };
    case UPDATE_USER_EVENT_ERROR:
      return {
        ...state,
        updateUserEventInProgress: false,
        updateUserEventError: payload,
      };

    // Delete user event
    case DELETE_USER_EVENT_REQUEST:
      return {
        ...state,
        deleteUserEventInProgress: true,
        deleteUserEventError: null,
      };
    case DELETE_USER_EVENT_SUCCESS:
      return {
        ...state,
        deleteUserEventInProgress: false,
        // Remove the deleted event from the events array
        events: state.events.filter(e => e.id !== payload.eventId),
      };
    case DELETE_USER_EVENT_ERROR:
      return {
        ...state,
        deleteUserEventInProgress: false,
        deleteUserEventError: payload,
      };

    // Delete episode
    case DELETE_EPISODE_REQUEST:
      return {
        ...state,
        deleteEpisodeInProgress: true,
        deleteEpisodeError: null,
      };
    case DELETE_EPISODE_SUCCESS:
      return {
        ...state,
        deleteEpisodeInProgress: false,
        // Remove the deleted episode from the events array
        events: state.events.filter(e => e.id !== payload.eventId),
      };
    case DELETE_EPISODE_ERROR:
      return {
        ...state,
        deleteEpisodeInProgress: false,
        deleteEpisodeError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchCalendarEventsRequest = () => ({
  type: FETCH_CALENDAR_EVENTS_REQUEST,
});

export const fetchCalendarEventsSuccess = (events, listingRefs) => ({
  type: FETCH_CALENDAR_EVENTS_SUCCESS,
  payload: { events, listingRefs },
});

export const fetchCalendarEventsError = error => ({
  type: FETCH_CALENDAR_EVENTS_ERROR,
  error: true,
  payload: error,
});

// User event action creators
export const createUserEventRequest = () => ({
  type: CREATE_USER_EVENT_REQUEST,
});

export const createUserEventSuccess = event => ({
  type: CREATE_USER_EVENT_SUCCESS,
  payload: { event },
});

export const createUserEventError = error => ({
  type: CREATE_USER_EVENT_ERROR,
  error: true,
  payload: error,
});

export const updateUserEventRequest = () => ({
  type: UPDATE_USER_EVENT_REQUEST,
});

export const updateUserEventSuccess = event => ({
  type: UPDATE_USER_EVENT_SUCCESS,
  payload: { event },
});

export const updateUserEventError = error => ({
  type: UPDATE_USER_EVENT_ERROR,
  error: true,
  payload: error,
});

export const deleteUserEventRequest = () => ({
  type: DELETE_USER_EVENT_REQUEST,
});

export const deleteUserEventSuccess = eventId => ({
  type: DELETE_USER_EVENT_SUCCESS,
  payload: { eventId },
});

export const deleteUserEventError = error => ({
  type: DELETE_USER_EVENT_ERROR,
  error: true,
  payload: error,
});

// Delete episode action creators
export const deleteEpisodeRequest = () => ({
  type: DELETE_EPISODE_REQUEST,
});

export const deleteEpisodeSuccess = eventId => ({
  type: DELETE_EPISODE_SUCCESS,
  payload: { eventId },
});

export const deleteEpisodeError = error => ({
  type: DELETE_EPISODE_ERROR,
  error: true,
  payload: error,
});

// ================ Helpers ================ //

// Match exception with metadata from listing's publicData.exceptions
const findExceptionMetadata = (exceptionId, listing) => {
  const exceptionsMetadata = listing?.attributes?.publicData?.exceptions || [];
  return exceptionsMetadata.find(meta => meta.id === exceptionId);
};

// Transform availability exceptions to FullCalendar event format for episodes
const exceptionToEvent = (exception, listing, currentUser) => {
  const listingTitle = listing?.attributes?.title || 'Untitled';
  const listingId = listing?.id?.uuid;
  const exceptionId = exception?.id?.uuid;

  // Get attributes from the exception
  const { start, end, seats } = exception?.attributes || {};

  // Get metadata from listing's publicData.exceptions
  const metadata = findExceptionMetadata(exceptionId, listing);

  // Extract metadata fields
  const title = metadata?.attributes?.title || listingTitle;
  const meeting = metadata?.attributes?.meeting || null;
  const access = metadata?.attributes?.access || 'free';
  const membershipTierId = metadata?.attributes?.membershipTierId || null;
  const timeZone = metadata?.attributes?.timeZone || null;

  // Get membership tier name if available
  const membershipTiers = listing?.attributes?.publicData?.membershipTiers || [];
  const tier = membershipTiers.find(t => t.id === membershipTierId);
  const membershipTierName = tier?.name || null;

  // Build attendees array with the listing owner (current user for creator calendar)
  const ownerId = currentUser?.id?.uuid;
  const ownerName = currentUser?.attributes?.profile?.displayName
    || `${currentUser?.attributes?.profile?.firstName || ''} ${currentUser?.attributes?.profile?.lastName || ''}`.trim()
    || 'Owner';

  const attendees = currentUser ? [{
    id: ownerId,
    userId: ownerId,
    name: ownerName,
    user: currentUser,
  }] : [];

  return {
    id: `episode-${exceptionId}`,
    title: title,
    start: new Date(start),
    end: new Date(end),
    extendedProps: {
      type: 'episode',
      listingId,
      parentLiveRoomId: listingId,
      listingTitle,
      exceptionId,
      // The episode's permanent id, which its chat is keyed by; it differs
      // from the slot id once the episode has been rescheduled.
      episodeId: episodeKeyOf(metadata) || exceptionId,
      seats: seats || 0,
      // Additional metadata
      meeting,
      access,
      membershipTierId,
      membershipTierName,
      timeZone,
      // Owner/attendees
      ownerId,
      attendees,
    },
  };
};

// Transform user event from publicData to FullCalendar format
const userEventToCalendarEvent = (userEvent, currentUser) => {
  const ownerId = currentUser?.id?.uuid;
  const ownerName = currentUser?.attributes?.profile?.displayName
    || `${currentUser?.attributes?.profile?.firstName || ''} ${currentUser?.attributes?.profile?.lastName || ''}`.trim()
    || 'Owner';

  // The organizer first, then everyone invited to the event. The participants
  // are saved on the event itself; dropping them here is why the pop-up only
  // ever listed the organizer.
  const owner = currentUser ? [{
    id: ownerId,
    userId: ownerId,
    name: ownerName,
    user: currentUser,
  }] : [];
  const invited = (Array.isArray(userEvent.attendees) ? userEvent.attendees : []).filter(
    a => a && a.id !== ownerId && a.userId !== ownerId
  );
  const attendees = [...owner, ...invited];

  return {
    id: userEvent.id,
    title: userEvent.title,
    start: new Date(userEvent.start),
    end: new Date(userEvent.end),
    allDay: userEvent.allDay || false,
    extendedProps: {
      type: 'user-event',
      description: userEvent.description || '',
      meeting: userEvent.meeting || null,
      location: userEvent.location || null,
      timeZone: userEvent.timeZone,
      ownerId,
      attendees,
      createdAt: userEvent.createdAt,
      updatedAt: userEvent.updatedAt,
    },
  };
};

// ================ Thunks ================ //

export const fetchCalendarEvents = () => async (dispatch, getState, sdk) => {
  dispatch(fetchCalendarEventsRequest());

  try {
    // Get current user for attendees info and user events
    const { currentUser } = getState().user;

    // 1. Get user-created events from the creator's own profile.
    // Filter to only show personal events (no orgId)
    const userCalendarEvents = readOwnCalendarEvents(currentUser);
    const personalEvents = userCalendarEvents.filter(event => !event.orgId);
    const userEvents = personalEvents.map(event => userEventToCalendarEvent(event, currentUser));

    // 2. Fetch user's own live-room listings
    const listingsResponse = await sdk.ownListings.query({
      pub_type: config.listingTypes['live-room'],
      include: ['images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': 1,
    });

    const listings = listingsResponse.data.data || [];
    const listingRefs = listings.map(l => ({ id: l.id, type: l.type }));

    if (listings.length === 0) {
      // No listings, just return user events
      dispatch(fetchCalendarEventsSuccess(userEvents, []));
      return;
    }

    // Calculate date range for fetching exceptions (max 366 days per API limit)
    const now = new Date();
    const start = new Date(now);
    start.setMonth(start.getMonth() - 3);
    const end = new Date(now);
    end.setMonth(end.getMonth() + 9);

    // 3. For each listing, fetch availability exceptions (episodes)
    const episodeEvents = [];

    for (const listing of listings) {
      const listingId = listing.id.uuid;

      try {
        const exceptionsResponse = await availabilityExceptionsAPI.query({
          listingId,
          start: start.toISOString(),
          end: end.toISOString(),
        });

        // API returns { data: { data: { data: [...], included: [...] } } }
        const responseData = exceptionsResponse?.data?.data || {};
        const exceptions = responseData?.data || [];

        // Transform exceptions to calendar events (pass currentUser for attendees)
        const events = exceptions.map(exception => exceptionToEvent(exception, listing, currentUser));

        episodeEvents.push(...events);
      } catch (e) {
        // Log error but continue with other listings
        console.error(`Error fetching exceptions for listing ${listingId}:`, e);
      }
    }

    // Combine user events and episode events
    const allEvents = [...userEvents, ...episodeEvents];

    dispatch(fetchCalendarEventsSuccess(allEvents, listingRefs));
  } catch (e) {
    dispatch(fetchCalendarEventsError(storableError(e)));
  }
};

// Create a new user event
export const createUserEvent = eventData => async (dispatch, getState, sdk) => {
  dispatch(createUserEventRequest());

  try {
    const { currentUser } = getState().user;
    const existingEvents = readOwnCalendarEvents(currentUser);

    // Add the new event to the array
    const updatedEvents = [...existingEvents, eventData];

    // Update user profile with the new events array
    await sdk.currentUser.updateProfile(ownCalendarEventsWrite(currentUser, updatedEvents));

    await sendInvitesIfWanted(eventData);

    // Refresh current user to get updated data (events and invite records)
    await dispatch(fetchCurrentUser());

    // Transform the event data to calendar format for the UI
    const calendarEvent = userEventToCalendarEvent(eventData, currentUser);

    dispatch(createUserEventSuccess(calendarEvent));

    // Re-fetch all calendar events to ensure consistency
    await dispatch(fetchCalendarEvents());

    return calendarEvent;
  } catch (e) {
    dispatch(createUserEventError(storableError(e)));
    throw e;
  }
};

// Update an existing user event
export const updateUserEvent = (eventId, eventData) => async (dispatch, getState, sdk) => {
  dispatch(updateUserEventRequest());

  try {
    const { currentUser } = getState().user;
    const existingEvents = readOwnCalendarEvents(currentUser);

    // Find and update the event
    const updatedEvents = existingEvents.map(event =>
      event.id === eventId ? { ...event, ...eventData } : event
    );

    // Update user profile with the updated events array
    await sdk.currentUser.updateProfile(ownCalendarEventsWrite(currentUser, updatedEvents));

    await sendInvitesIfWanted({ ...eventData, id: eventId });

    // Refresh current user to get updated data (events and invite records)
    await dispatch(fetchCurrentUser());

    // Get the updated current user
    const updatedUser = getState().user.currentUser;

    // Transform the event data to calendar format for the UI
    const calendarEvent = userEventToCalendarEvent(eventData, updatedUser);

    dispatch(updateUserEventSuccess(calendarEvent));

    // Re-fetch all calendar events to ensure consistency
    await dispatch(fetchCalendarEvents());

    return calendarEvent;
  } catch (e) {
    dispatch(updateUserEventError(storableError(e)));
    throw e;
  }
};

// Delete a user event
export const deleteUserEvent = eventId => async (dispatch, getState, sdk) => {
  dispatch(deleteUserEventRequest());

  try {
    const { currentUser } = getState().user;
    const existingEvents = readOwnCalendarEvents(currentUser);

    // Remove the event from the array
    const updatedEvents = existingEvents.filter(event => event.id !== eventId);

    // Update user profile with the filtered events array
    await sdk.currentUser.updateProfile(ownCalendarEventsWrite(currentUser, updatedEvents));

    // Refresh current user to get updated data
    await dispatch(fetchCurrentUser());

    dispatch(deleteUserEventSuccess(eventId));

    // Re-fetch all calendar events to ensure consistency
    await dispatch(fetchCalendarEvents());

    return eventId;
  } catch (e) {
    dispatch(deleteUserEventError(storableError(e)));
    throw e;
  }
};

// Delete an episode (availability exception)
export const deleteEpisode = (exceptionId, listingId) => async (dispatch, getState, sdk) => {
  dispatch(deleteEpisodeRequest());

  try {
    // 1. Delete the availability exception via API
    await availabilityExceptionsAPI.delete({ id: exceptionId, listingId });

    // 2. Remove the metadata from listing.publicData.exceptions
    // First, fetch the current listing to get existing exceptions metadata
    const listingResponse = await sdk.ownListings.query({
      ids: [listingId],
    });

    const listing = listingResponse?.data?.data?.[0];
    if (listing) {
      const existingExceptions = listing?.attributes?.publicData?.exceptions || [];
      const updatedExceptions = existingExceptions.filter(ex => ex.id !== exceptionId);

      // Update the listing with filtered exceptions metadata
      await sdk.ownListings.update({
        id: listing.id,
        publicData: {
          exceptions: updatedExceptions,
        },
      });
    }

    // 3. Dispatch success with the calendar event ID
    const eventId = `episode-${exceptionId}`;
    dispatch(deleteEpisodeSuccess(eventId));

    // 4. Re-fetch all calendar events to ensure consistency
    await dispatch(fetchCalendarEvents());

    return eventId;
  } catch (e) {
    dispatch(deleteEpisodeError(storableError(e)));
    throw e;
  }
};

// Create-event participant options. A creator has no team, so nothing is
// preloaded: people are found one at a time through searchParticipants.
export const fetchParticipantOptions = () => async dispatch => {
  dispatch(setParticipantOptions([]));
};

/**
 * Participant search over the creator's own audience, on the server, where
 * the tier rules live (server/api-util/calendarInvites.js). Returns options
 * for the picker; nothing is kept in state.
 */
export const searchParticipants = q => async () => {
  const res = await calendarInvitesAPI.searchCreatorParticipants(q);
  return res?.data?.data?.options || [];
};

export const loadData = () => async (dispatch, getState, sdk) => {
  // Ensure user is logged in and we have fresh data
  await dispatch(fetchCurrentUser());

  // Participant options for the Create-Event modal (non-blocking).
  dispatch(fetchParticipantOptions());

  // Now fetch calendar events with the user data available
  return dispatch(fetchCalendarEvents());
};
